import { useState, useEffect, useCallback, useRef } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import {
  Users,
  Plus,
  Mail,
  Trash2,
  Copy,
  Check,
  Shield,
  AlertCircle
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamPage() {
  const { getToken, user } = useTenant();
  const dashData = useDashboardData();
  const preloaded = dashData?.team;
  const [teamData, setTeamData] = useState(preloaded || { members: [], invites: [] });
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(null);
  const skipInitialFetch = useRef(!!preloaded);
  
  // Invite Modal
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('staff');
  const [isInviting, setIsInviting] = useState(false);
  
  // Copy Link State
  const [copiedLink, setCopiedLink] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch team');
      const data = await response.json();
      setTeamData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to send invite');
      }
      
      setInviteEmail('');
      setIsInviteModalOpen(false);
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevokeInvite = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this invite?')) return;
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/team/invite/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to revoke invite');
      setError(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemoveMember = async (id) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/team/member/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to remove member');
      setError(null);
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/accept-invite?token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(token);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="team-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-600 mt-1">Manage access to your business account</p>
        </div>
        <button
          onClick={() => setIsInviteModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Invite Member
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
            {[1,2].map(i => <div key={i} className="flex items-center gap-4"><Skeleton className="h-10 w-10 rounded-full" /><div className="flex-1 space-y-2"><Skeleton className="h-4 w-40" /><Skeleton className="h-3 w-24" /></div></div>)}
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Members Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Active Members ({teamData.members.length})</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {teamData.members.map(member => (
                <div key={member.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">
                      {(member.first_name?.[0] || member.email[0]).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {member.first_name} {member.last_name}
                        {member.id === user?.id && <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">You</span>}
                      </div>
                      <div className="text-sm text-gray-500">{member.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 capitalize">
                      <Shield className="w-3 h-3" />
                      {member.role}
                    </span>
                    {/* Only show delete button for non-owners and not for yourself */}
                    {member.role !== 'owner' && member.id !== user?.id && (
                      <button 
                        onClick={() => handleRemoveMember(member.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Remove Member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Invites Section */}
          {teamData.invites.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-900">Pending Invites ({teamData.invites.length})</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {teamData.invites.map(invite => (
                  <div key={invite.id} className="p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-500">
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{invite.email}</div>
                        <div className="text-sm text-gray-500">Expires {new Date(invite.expires_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500 capitalize">{invite.role}</span>
                      
                      <button 
                        onClick={() => copyInviteLink(invite.token)}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        {copiedLink === invite.token ? (
                          <>
                            <Check className="w-4 h-4" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> Copy Link
                          </>
                        )}
                      </button>

                      <button 
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Revoke Invite"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">Invite Team Member</h2>
            <form onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="colleague@company.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  <option value="staff">Staff (Can send campaigns)</option>
                  <option value="admin">Admin (Full access except billing)</option>
                  <option value="owner">Owner (Full access)</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsInviteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isInviting}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {isInviting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Generate Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
